/**
 * Record-level form access and controlled (CUI) data, the browser side.
 *
 * The rules live in the database (migration 00535): row security decides who
 * reads a request, a run or a document, and the RPCs here are the only way to
 * change access settings or a CUI marking. This module wraps those RPCs and
 * holds the pure helpers the screens share (principal parsing, the CUI field
 * mapping, the CSV marking, generic notice text), so the UI and the database
 * apply the same wording and the same "yes" answers.
 */
import { supabase } from '@/lib/supabase'

/**
 * XPM: CUI handling is a deployment setting. The browser shows CUI controls
 * only when the build sets VITE_CUI_ENABLED=true (the Azure enterprise build)
 * AND the database has CUI handling switched on (wff_cui_deployment, migration
 * 00535). The Vercel build leaves the variable unset, so the controls stay
 * hidden there, and the database refuses to flag records either way.
 */
export function cuiBuildEnabled(): boolean {
  return String(import.meta.env.VITE_CUI_ENABLED ?? '').trim().toLowerCase() === 'true'
}

export type AccessMode = 'restricted' | 'org'
export type CuiCategory = 'CUI' | 'FOUO' | 'ITAR' | 'EAR'
export const CUI_CATEGORIES: Array<{ value: CuiCategory; label: string }> = [
  { value: 'CUI', label: 'CUI (Controlled Unclassified Information)' },
  { value: 'FOUO', label: 'FOUO (For Official Use Only)' },
  { value: 'ITAR', label: 'ITAR (export-controlled, defense articles)' },
  { value: 'EAR', label: 'EAR (export-controlled, dual use)' },
]

/** Roles a principal list can name (role:<Role>): access roles and system roles. */
export const ACCESS_ROLE_OPTIONS = ['GlobalAdmin', 'OrgAdmin', 'ConfigAdmin', 'GlobalViewer', 'Viewer', 'User'] as const

/** One form family's settings, as Setup > Access and CUI edits them. */
export interface FormAccessSettings {
  accessMode: AccessMode
  submitPrincipals: string[]
  viewerPrincipals: string[]
  managerPrincipals: string[]
  organizationId: string | null
  cuiCapable: boolean
  cuiAll: boolean
  cuiFieldKey: string | null
  cuiFieldValues: string[]
  cuiCategories: CuiCategory[]
}

export interface AccessReportField { key: string; label: string; type: string; options: string[] }

export interface AccessReportForm extends FormAccessSettings {
  familyKey: string
  formId: string
  name: string
  version: number
  status: string
  ownerId: string | null
  isPublic: boolean
  sitePolicy: boolean
  engineeringPolicy: boolean
  hasSetting: boolean
  legacyDefault: boolean
  reviewedBy: string | null
  reviewedAt: string | null
  submissions: number
  cuiSubmissions: number
  fields: AccessReportField[]
}

export interface CuiSettingsRow {
  organizationId: string
  groupId: string | null
  groupName: string | null
  members: string[]
  marking: string
  updatedBy: string | null
  updatedAt: string | null
}

export interface CuiAuditRow {
  id: string
  at: string
  actor: string | null
  action: string
  entityType: string | null
  entityId: string | null
  summary: string
  severity: string
  payload: Record<string, unknown>
}

export interface AccessReport {
  /** CUI handling is on for this build and database. */
  cuiEnabled: boolean
  forms: AccessReportForm[]
  cuiSettings: CuiSettingsRow[]
  cuiDocuments: number
  recentCuiAudit: CuiAuditRow[]
}

/** What the signed-in person may do on one form (wff_my_form_access). */
export interface MyFormAccess {
  familyKey: string | null
  accessMode: AccessMode
  legacyDefault: boolean
  ownPolicy: boolean
  canSubmit: boolean
  canViewAll: boolean
  canManage: boolean
  /** CUI handling is on for this build and database. */
  cuiEnabled: boolean
  cuiCapable: boolean
  cuiAll: boolean
  cuiFieldKey: string | null
  cuiFieldValues: string[]
  cuiCategories: CuiCategory[]
  cuiConfigured: boolean
  cuiMember: boolean
  marking: string
}

/** `enabled`: CUI handling is on for this build and database (see cuiBuildEnabled). */
export interface MyCuiStatus { enabled: boolean; configured: boolean; member: boolean; admin: boolean; marking: string }

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : [])

/** Split a typed principal list ("@Quality, role:OrgAdmin, Pat Lee") into tokens. */
export function parsePrincipals(text: string): string[] {
  const out: string[] = []
  for (const raw of text.split(/[,\n;]/)) {
    const t = raw.trim()
    if (t && t.length <= 200 && !out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t)
  }
  return out
}

/** A principal token in words, for tables and the access summary. */
export function describePrincipal(token: string): string {
  const t = token.trim()
  if (t === '*') return 'Everyone in the organization'
  if (t.startsWith('@')) return `Group: ${t.slice(1).trim()}`
  if (/^role:/i.test(t)) return `Role: ${t.slice(5).trim()}`
  if (/^org:/i.test(t)) return `Organization: ${t.slice(4).trim()}`
  return t
}

/** Who sees all requests of a form, in one sentence. */
export function describeAccess(s: Pick<FormAccessSettings, 'accessMode' | 'viewerPrincipals' | 'managerPrincipals'>): string {
  if (s.accessMode === 'org') return 'Everyone in the organization sees all requests.'
  const extra = [...s.managerPrincipals, ...s.viewerPrincipals].map(describePrincipal)
  return `Submitters see their own requests, approvers see the requests routed to them, and the form owner, administrators${extra.length ? `, ${extra.join(', ')}` : ''} see all of them.`
}

/** The answers that mark a request controlled (default: Yes / true). */
export function cuiYesValues(values: string[] | null | undefined): string[] {
  const yes = strings(values).map((v) => v.toLowerCase())
  return yes.length > 0 ? yes : ['yes', 'true']
}

/**
 * Do these answers make a request controlled? Mirrors
 * public.wff_values_require_cui so the submit page can warn before the
 * database decides.
 */
export function valuesRequireCui(
  access: Pick<FormAccessSettings, 'cuiCapable' | 'cuiAll' | 'cuiFieldKey' | 'cuiFieldValues'> | null | undefined,
  values: Record<string, unknown> | null | undefined,
): boolean {
  if (!access?.cuiCapable) return false
  if (access.cuiAll) return true
  const key = access.cuiFieldKey?.trim()
  if (!key || !values) return false
  const raw = values[key]
  if (raw == null) return false
  const yes = cuiYesValues(access.cuiFieldValues)
  return (Array.isArray(raw) ? raw : [raw]).some((v) => {
    if (v === true) return yes.includes('yes') || yes.includes('true')
    if (v == null || typeof v === 'object') return false
    return yes.includes(String(v).trim().toLowerCase())
  })
}

/** Is a workflow run carrying controlled data (context.cui, stamped by the database)? */
export function isCuiRun(run: { context?: Record<string, unknown> | null } | null | undefined): boolean {
  return run?.context?.cui === true
}

/** The banner line for a controlled record: the organization's marking first. */
export function cuiBannerText(marking: string | null | undefined, categories: CuiCategory[] = []): string {
  const head = (marking ?? '').trim() || 'CUI'
  const extra = categories.filter((c) => c !== 'CUI' && !head.toUpperCase().includes(c))
  return `${head}${extra.length ? ` / ${extra.join(' / ')}` : ''} - Controlled information. Handle and share only with authorized people.`
}

/**
 * Prepend the marking rows to a CSV export that contains controlled records.
 * Rows without a controlled record are returned unchanged.
 */
export function markCsvRows<T>(rows: T[][], opts: { cuiCount: number; marking: string | null | undefined }): Array<Array<T | string>> {
  if (opts.cuiCount <= 0) return rows
  const banner = cuiBannerText(opts.marking)
  return [[banner], [`This export contains ${opts.cuiCount} controlled record${opts.cuiCount === 1 ? '' : 's'}.`], [], ...rows, [], [banner]]
}

/** Generic text used wherever a controlled request would otherwise be described. */
export const CUI_GENERIC_TITLE = 'A controlled request needs your review'
export const CUI_GENERIC_MESSAGE = 'Details are withheld because this request is marked as controlled (CUI). Open the app to view it.'

/** Forms an admin should look at first: still on the legacy default, or CUI without a mapping. */
export function reportAttention(forms: AccessReportForm[]): { legacy: AccessReportForm[]; cuiUnmapped: AccessReportForm[] } {
  return {
    legacy: forms.filter((f) => f.legacyDefault && !f.sitePolicy && !f.engineeringPolicy),
    cuiUnmapped: forms.filter((f) => f.cuiCapable && !f.cuiAll && !f.cuiFieldKey),
  }
}

function toSettings(r: Record<string, unknown>): FormAccessSettings {
  return {
    accessMode: r.accessMode === 'org' ? 'org' : 'restricted',
    submitPrincipals: strings(r.submitPrincipals),
    viewerPrincipals: strings(r.viewerPrincipals),
    managerPrincipals: strings(r.managerPrincipals),
    organizationId: (r.organizationId as string) ?? null,
    cuiCapable: r.cuiCapable === true,
    cuiAll: r.cuiAll === true,
    cuiFieldKey: (r.cuiFieldKey as string) || null,
    cuiFieldValues: strings(r.cuiFieldValues),
    cuiCategories: strings(r.cuiCategories) as CuiCategory[],
  }
}

/** Parse wff_access_report() defensively: a missing list is an empty one. */
export function parseAccessReport(raw: unknown): AccessReport {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const list = (v: unknown) => (Array.isArray(v) ? (v as Record<string, unknown>[]) : [])
  return {
    cuiEnabled: cuiBuildEnabled() && r.cuiEnabled === true,
    forms: list(r.forms).map((f) => ({
      ...toSettings(f),
      familyKey: String(f.familyKey ?? ''),
      formId: String(f.formId ?? ''),
      name: String(f.name ?? 'Untitled form'),
      version: Number(f.version ?? 1),
      status: String(f.status ?? 'published'),
      ownerId: (f.ownerId as string) ?? null,
      isPublic: f.isPublic === true,
      sitePolicy: f.sitePolicy === true,
      engineeringPolicy: f.engineeringPolicy === true,
      hasSetting: f.hasSetting === true,
      legacyDefault: f.legacyDefault === true,
      reviewedBy: (f.reviewedBy as string) ?? null,
      reviewedAt: (f.reviewedAt as string) ?? null,
      submissions: Number(f.submissions ?? 0),
      cuiSubmissions: Number(f.cuiSubmissions ?? 0),
      fields: list(f.fields).map((x) => ({ key: String(x.key ?? ''), label: String(x.label ?? x.key ?? ''), type: String(x.type ?? ''), options: strings(x.options) })).filter((x) => x.key),
    })),
    cuiSettings: list(r.cuiSettings).map((s) => ({
      organizationId: String(s.organizationId ?? 'default'),
      groupId: (s.groupId as string) ?? null,
      groupName: (s.groupName as string) ?? null,
      members: strings(s.members),
      marking: String(s.marking ?? 'CUI'),
      updatedBy: (s.updatedBy as string) ?? null,
      updatedAt: (s.updatedAt as string) ?? null,
    })),
    cuiDocuments: Number(r.cuiDocuments ?? 0),
    recentCuiAudit: list(r.recentCuiAudit).map((a) => ({
      id: String(a.id ?? ''),
      at: String(a.at ?? ''),
      actor: (a.actor as string) ?? null,
      action: String(a.action ?? ''),
      entityType: (a.entityType as string) ?? null,
      entityId: (a.entityId as string) ?? null,
      summary: String(a.summary ?? ''),
      severity: String(a.severity ?? 'info'),
      payload: (a.payload && typeof a.payload === 'object' ? a.payload : {}) as Record<string, unknown>,
    })),
  }
}

export function parseMyFormAccess(raw: unknown): MyFormAccess {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const s = toSettings(r)
  const enabled = cuiBuildEnabled() && r.cuiEnabled === true
  return {
    familyKey: (r.familyKey as string) ?? null,
    accessMode: s.accessMode,
    legacyDefault: r.legacyDefault === true,
    ownPolicy: r.ownPolicy === true,
    canSubmit: r.canSubmit !== false,
    canViewAll: r.canViewAll === true,
    canManage: r.canManage === true,
    cuiEnabled: enabled,
    cuiCapable: enabled && s.cuiCapable,
    cuiAll: s.cuiAll,
    cuiFieldKey: s.cuiFieldKey,
    cuiFieldValues: s.cuiFieldValues,
    cuiCategories: s.cuiCategories,
    cuiConfigured: enabled && r.cuiConfigured === true,
    cuiMember: enabled && r.cuiMember === true,
    marking: String(r.marking ?? 'CUI'),
  }
}

// ---------------------------------------------------------------------------
// RPCs
// ---------------------------------------------------------------------------

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return data as T
}

export async function getAccessReport(): Promise<AccessReport> {
  return parseAccessReport(await rpc('wff_access_report'))
}

export async function getMyFormAccess(formId: string): Promise<MyFormAccess> {
  return parseMyFormAccess(await rpc('wff_my_form_access', { p_form_id: formId }))
}

export async function getMyCuiStatus(): Promise<MyCuiStatus> {
  const r = (await rpc<Record<string, unknown> | null>('wff_my_cui_status')) ?? {}
  const enabled = cuiBuildEnabled() && r.enabled === true
  return { enabled, configured: enabled && r.configured === true, member: enabled && r.member === true, admin: r.admin === true, marking: String(r.marking ?? 'CUI') }
}

export async function saveFormAccess(familyKey: string, settings: FormAccessSettings): Promise<void> {
  await rpc('wff_save_form_access', { p_family_key: familyKey, p_settings: settings })
}

export async function saveCuiSettings(organizationId: string, groupId: string | null, marking: string): Promise<void> {
  await rpc('wff_save_cui_settings', { p_organization_id: organizationId, p_group_id: groupId, p_marking: marking })
}

export async function setSubmissionCui(submissionId: string, cui: boolean, reason?: string): Promise<boolean> {
  const r = await rpc<{ changed?: boolean }>('wff_set_submission_cui', { p_submission_id: submissionId, p_cui: cui, p_reason: reason ?? null })
  return r?.changed === true
}

export async function setDocumentCui(documentId: string, cui: boolean, reason?: string): Promise<boolean> {
  const r = await rpc<{ changed?: boolean }>('wff_set_document_cui', { p_document_id: documentId, p_cui: cui, p_reason: reason ?? null })
  return r?.changed === true
}

export type CuiAccessAction = 'cui.view' | 'cui.export' | 'cui.print' | 'cui.download' | 'cui.ai_blocked'

/**
 * Record a view, export, print or blocked AI call of a controlled record.
 * Best effort: an audit write that fails is logged to the console and never
 * blocks the screen (the database ignores records that are not controlled).
 */
export async function logCuiAccess(entityType: 'form_submission' | 'workflow_run' | 'document', entityId: string, action: CuiAccessAction, detail: Record<string, unknown> = {}): Promise<void> {
  try {
    await rpc('wff_log_cui_access', { p_entity_type: entityType, p_entity_id: entityId, p_action: action, p_detail: detail })
  } catch (err) {
    console.warn('[cui] access log failed:', err)
  }
}
